import React, { useState, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { atom, useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

export interface FilterProps {
  'order.name'?: string;
  'order.sort'?: 'DESC' | 'ASC';
}
export interface PaginationProps {
  countPerPage: number;
  currentPage: number;
  totalCount?: number;
}

export type ConditionProps<T> = T & FilterProps & PaginationProps;
export interface PaginationProps {
  countPerPage: number;
  currentPage: number;
  totalCount?: number;
}

export const DEFAULT_COUNT_PER_PAGE = 10;
export const DEFAULT_CURRENT_PAGE = 1;

export const conditionState = atom<ConditionProps<any>>({
  key: 'conditionState',
  default: {
    countPerPage: DEFAULT_COUNT_PER_PAGE,
    currentPage: DEFAULT_CURRENT_PAGE,
    'order.name': 'id',
    'order.sort': 'DESC',
  },
});

export const paginationSummaryState = atom<PaginationProps>({
  key: 'paginationSummaryState',
  default: {
    countPerPage: DEFAULT_COUNT_PER_PAGE,
    currentPage: DEFAULT_CURRENT_PAGE,
  },
});

const RENDER_PAGE_COUNT_PER_PAGE = 5; // ex) << < 1 2 3 4 5 > >>
const NO_DATA_PAGE_DEPTH = 0;
const NO_DATA_DEFAULT_PAGE = 1;
const CONVERT_TO_PAGE_NUMBER = 1;
const CONVERT_TO_INDEX = -1;
const TO_NEXT_PAGE = 1;
const TO_PREV_PAGE = -1;

export const Pagination = ({ conditions, setConditions }: any) => {
  const displayPageNumber =
    Math.min(
      Math.ceil((conditions.totalCount || conditions.countPerPage) / conditions.countPerPage),
      RENDER_PAGE_COUNT_PER_PAGE,
    ) || NO_DATA_DEFAULT_PAGE;

  const pageDepth =
    Math.floor((conditions.currentPage + CONVERT_TO_INDEX) / displayPageNumber) * displayPageNumber ||
    NO_DATA_PAGE_DEPTH;

  const RenderPageNumbers = (pageNum: any) =>
    Array(pageNum)
      .fill('')
      .filter((_, index) => {
        let displayPage = index + CONVERT_TO_PAGE_NUMBER + pageDepth;
        let lastPage = Math.ceil((conditions.totalCount || 0) / conditions.countPerPage);

        return displayPage <= lastPage;
      })
      .map((_, index) => {
        let displayPage = index + CONVERT_TO_PAGE_NUMBER + pageDepth;
        return (
          <div
            key={uuid()}
            data-active={`${displayPage === conditions.currentPage}`}
            onClick={() => {
              setConditions((prev: ConditionProps<any>) => ({
                ...prev,
                currentPage: displayPage,
              }));
            }}
          >
            {displayPage}
          </div>
        );
      });
  return (
    <PaginationWrapper>
      <PagingSection>
        <div
          onClick={() => {
            if (conditions.currentPage === 1) return;
            setConditions((prev: ConditionProps<any>) => ({
              ...prev,
              currentPage: 1,
            }));
          }}
        >
          {/* << :before, :after 에서 적용 */}
        </div>
        <div
          onClick={() => {
            if (conditions.currentPage === 1) return;
            setConditions((prev: ConditionProps<any>) => ({
              ...prev,
              currentPage: prev.currentPage + TO_PREV_PAGE,
            }));
          }}
        >
          {'<'}
        </div>
        {RenderPageNumbers(displayPageNumber)}
        <div
          onClick={() => {
            if (conditions.currentPage >= (conditions.totalCount || 0) / conditions.countPerPage) return;
            setConditions((prev: ConditionProps<any>) => ({
              ...prev,
              currentPage: prev.currentPage + 1,
            }));
          }}
        >
          {'>'}
        </div>
        <div
          onClick={() => {
            if (conditions.currentPage >= (conditions.totalCount || 0) / conditions.countPerPage) return;

            let lastPage = Math.floor((conditions.totalCount || 0) / conditions.countPerPage) + 1;
            if ((conditions.totalCount || 0) % conditions.countPerPage === 0) {
              lastPage -= 1;
            }
            setConditions((prev: ConditionProps<any>) => ({
              ...prev,
              currentPage: lastPage,
            }));
          }}
        >
          {/* >> :before, :after 에서 적용 */}
        </div>
      </PagingSection>
    </PaginationWrapper>
  );
};

//#region styled component
const FlexCenterSetCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const PaginationWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-content: space-between;
  margin: 10px 30px;
  position: relative;
  font: normal normal normal 12px/20px Noto Sans KR;
  color: #ffffff;
`;
const SelectCombo = styled.div`
  ${FlexCenterSetCss};
  margin: 0px 10px;
  label {
    margin-right: 10px;
  }
  select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    height: 30px;
    color: white;
    font: normal normal normal 14px/20px Noto Sans KR;
    padding: 0px 10px;
    & option {
      background-color: #000000;
      color: #ffffff;
    }
  }
`;
const DisplayPerPage = styled(SelectCombo)`
  display: flex;
  justify-content: flex-start;
  label {
    width: 35px;
  }
  select {
    width: 100px;
    height: 30px;
    margin: 0 10px;
  }
`;
const PageSummary = styled.div`
  color: #cccccc;
`;
const PagingSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  & > div {
    cursor: pointer;
    ${FlexCenterSetCss};
    color: #999999;
    margin: 0 0.5px;
    min-width: 24px;
    height: 24px;
    border-radius: 12px;
    box-sizing: border-box;
    padding: 0 8px;

    :hover {
      background: #ffffff;
      &[data-active='true'] {
        background: #2de100;
      }
    }
    &[data-active='true'] {
      color: #ffffff;
      background: #7fe200;
    }
  }
  div:nth-of-type(1) {
    color: #999999;
    width: 24px !important;
    position: relative;
    :after,
    :before {
      position: absolute;
      content: '<';
    }
    :before {
      left: 20%;
    }
    :after {
      right: 20%;
    }
  }
  div:nth-last-of-type(1) {
    color: #999999;
    width: 24px !important;
    position: relative;
    :after,
    :before {
      position: absolute;
      content: '>';
    }
    :before {
      left: 20%;
    }
    :after {
      right: 20%;
    }
  }
`;
//#endregion

export default Pagination;
